import { motion } from 'framer-motion'
import { useEffect, useRef, type ReactNode } from 'react'
import { MOTION } from '../lib/constants'
import { useLayout } from '../hooks/useLayout'
import { useVisualViewport } from '../hooks/useVisualViewport'
import { fitSheet } from '../lib/sheetFit'

/**
 * The shell every sheet shares. On the phone it is a bottom sheet with a
 * grabber that slides up (320 ms, ease-out, no spring); on desktop and iPad it
 * is a centred dialog that scales in from .97 (220 ms) — ux/03 ItemEditor,
 * ux/04 Other motion. Escape closes it (scoped to the panel, not a global
 * listener) and focus returns to whatever opened it.
 *
 * It deliberately does **not** take `open`. Each caller keeps its own
 * `<AnimatePresence>` and its own `{open && <Body/>}` split, because several
 * sheets rely on that split to unmount their body and reset its state on
 * close (see settings/SettingsSheet). A shell that owned `open` would have to render
 * those bodies while closed, which is the one thing this must not change.
 *
 * On the phone every sheet keeps above the keyboard: it sits on it and fits
 * the visible height (lib/sheetFit.ts), `is-keyboard` dropping the home-bar
 * padding and letting a plain sheet scroll. Fields that take focus on open use
 * hooks/useAutoFocus, so iOS doesn't scroll the header away.
 */
export function SheetPanel({
  onClose, children, zIndex = 10, maxHeight, layout = 'plain', role = 'dialog', ariaLabel, wide = false,
}: {
  onClose: () => void
  children: ReactNode
  /** The scrim sits here, the panel one above. Raised when a sheet opens over another. */
  zIndex?: number
  maxHeight?: string
  /**
   * 'scroll' scrolls the panel itself. 'column' leaves it a flex column so a
   * child list can scroll under a footer that stays put.
   */
  layout?: 'plain' | 'scroll' | 'column'
  role?: string
  ariaLabel?: string
  /** Desktop: the 900 px dialog (recipe detail, the choices) instead of 520 px. */
  wide?: boolean
}) {
  const dialog = useLayout() === 'desktop'
  const panel = useRef<HTMLDivElement>(null)
  const vv = useVisualViewport(!dialog)
  const fit = !dialog && vv ? fitSheet({ innerHeight: window.innerHeight, vvHeight: vv.height, vvTop: vv.offsetTop }) : null

  // Focus moves into the panel (unless a field inside took it with
  // hooks/useAutoFocus) and goes back to the trigger on close.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null
    if (!panel.current?.contains(document.activeElement)) panel.current?.focus({ preventScroll: true })
    return () => { if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true }) }
  }, [])

  const base = dialog ? 'ml-dialog' : 'ml-sheet'
  const className = `${base}${layout === 'scroll' ? ` ${base}--scroll` : ''}${dialog && wide ? ' ml-dialog--wide' : ''}${fit ? ' is-keyboard' : ''}`
  const sizing = fit
    ? { bottom: `${fit.bottom}px`, maxHeight: maxHeight ? `min(${maxHeight}, ${fit.maxHeight}px)` : `${fit.maxHeight}px` }
    : maxHeight ? { maxHeight } : {}
  const motionProps = dialog
    ? {
        initial: { opacity: 0, scale: MOTION.dialog.fromScale, x: '-50%', y: '-50%' },
        animate: { opacity: 1, scale: 1, x: '-50%', y: '-50%' },
        exit: { opacity: 0, scale: MOTION.dialog.fromScale, x: '-50%', y: '-50%' },
        transition: { duration: MOTION.dialog.ms / 1000, ease: [0.2, 0.8, 0.2, 1] as const },
      }
    : {
        initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' },
        transition: { duration: MOTION.sheet.ms / 1000, ease: [0.2, 0.8, 0.2, 1] as const },
      }

  return (
    <>
      <motion.div
        className="ml-scrim"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        style={{ zIndex }}
      />
      <motion.div
        ref={panel}
        role={role}
        aria-modal={role === 'dialog' ? true : undefined}
        aria-label={ariaLabel}
        tabIndex={-1}
        className={className}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }}
        style={{ zIndex: zIndex + 1, ...sizing }}
        {...motionProps}
      >
        <div className="ml-grabber" aria-hidden="true" />
        {children}
      </motion.div>
    </>
  )
}
